// Central service catalogue — reused by Home and Services pages.
// `icon` is a lucide-react component name (imported at usage site).
export const SERVICE_CATALOG = [
  {
    slug: "product-sourcing",
    number: "01",
    icon: "PackageSearch",
    title: "Product Sourcing",
    short: "Find the right product, at the right cost, from the right factory.",
    value:
      "Reduce sourcing time and unit cost while meeting your quality and compliance targets.",
    detail:
      "We translate your specification into a shortlist of qualified factories, secure quotes, run samples, and align commercial terms — before you sign anything.",
  },
  {
    slug: "supplier-sourcing",
    number: "02",
    icon: "Radar",
    title: "Supplier Sourcing",
    short:
      "Identify qualified manufacturers matched to your capacity and standards.",
    value:
      "Skip weeks of database searches and move directly to shortlisted, capable suppliers.",
    detail:
      "Country, capacity, certifications, capability, price bracket. We narrow the field on the criteria that matter and hand you a comparable shortlist.",
  },
  {
    slug: "supplier-verification",
    number: "03",
    icon: "ShieldCheck",
    title: "Supplier Verification",
    short: "On-the-ground checks before you commit purchase orders.",
    value:
      "Eliminate the risk of paying an intermediary, a shell company, or a factory that cannot deliver.",
    detail:
      "Legal standing, ownership, real production capability, quality systems, workforce, references. Verified in person, reported in writing.",
  },
  {
    slug: "manufacturing-services",
    number: "04",
    icon: "Cog",
    title: "Manufacturing Services",
    short: "Build durable manufacturing partnerships in Asia.",
    value:
      "Convert a one-off order into a stable, scalable production relationship.",
    detail:
      "Structuring long-term programs, negotiating commercial terms, aligning specifications, and defining a governance model both sides respect.",
  },
  {
    slug: "production-management",
    number: "05",
    icon: "LineChart",
    title: "Production Management",
    short: "Day-to-day oversight so your production stays on plan.",
    value:
      "Fewer surprises, tighter timelines, and a single accountable point of contact.",
    detail:
      "Materials, milestones, capacity, escalations. We run the production floor conversation so your team stays focused on the business.",
  },
  {
    slug: "quality-control",
    number: "06",
    icon: "BadgeCheck",
    title: "Quality Control",
    short:
      "Pre-production, in-line, and pre-shipment inspections against your specification.",
    value:
      "Ship what you approved. Catch deviations before they reach your customers.",
    detail:
      "AQL-based sampling, defect classification, photographic reports, and clear go / no-go decisions signed off before dispatch.",
  },
  {
    slug: "factory-inspection",
    number: "07",
    icon: "ClipboardCheck",
    title: "Factory Inspection",
    short: "Independent audits of capacity, quality systems, and compliance.",
    value:
      "Objective evidence for internal committees, insurers, and buyers who need proof.",
    detail:
      "Capability audits, social compliance, environmental practice, and process maturity — reported against recognised frameworks.",
  },
  {
    slug: "trading-services",
    number: "08",
    icon: "ArrowLeftRight",
    title: "Trading Services",
    short: "Structured international trading, executed end to end.",
    value:
      "One counterparty, one contract, one invoice — for cross-border transactions that would otherwise involve five.",
    detail:
      "Pricing, contracts, payment terms, incoterms, settlement, documentation. Cross-border trade handled as an execution problem, not a paperwork problem.",
  },
  {
    slug: "import-export-support",
    number: "09",
    icon: "Globe2",
    title: "Import & Export Support",
    short:
      "Compliance and coordination to move goods from Asia to global markets.",
    value:
      "Predictable transit, correct documentation, and clean customs — every shipment.",
    detail:
      "HS codes, certificates of origin, commercial invoices, packing lists, and coordination with your freight partners. We are not a freight forwarder — we are the operator that makes freight forwarders work correctly.",
  },
  {
    slug: "business-representation",
    number: "10",
    icon: "UsersRound",
    title: "Business Representation",
    short: "Your local presence in Asia — without opening an office.",
    value:
      "Same-timezone response, on-site presence, and a professional interlocutor for every partner.",
    detail:
      "Meetings, negotiations, escalations, relationship management. We show up in person, in your name, on your terms.",
  },
];

export const INDUSTRIES = [
  { name: "Consumer Electronics", desc: "Components, assembly, and finished devices." },
  { name: "Apparel & Textiles", desc: "Fabric sourcing, garment manufacturing, private label." },
  { name: "Home & Lifestyle", desc: "Homeware, furniture, and lifestyle categories." },
  { name: "Industrial Goods", desc: "Machinery parts, tooling, and industrial components." },
  { name: "Beauty & Personal Care", desc: "Formulation, packaging, and contract manufacturing." },
  { name: "Retail Private Label", desc: "Brand-ready programs for retail and DTC operators." },
];

export const PROCESS_STEPS = [
  {
    n: "01",
    title: "Discovery",
    body: "We understand your product, targets, constraints, and current suppliers.",
  },
  {
    n: "02",
    title: "Sourcing & Verification",
    body: "We identify and validate suppliers on the ground — no directories, no guesswork.",
  },
  {
    n: "03",
    title: "Structuring",
    body: "Commercial terms, quality specifications, and timelines agreed in writing.",
  },
  {
    n: "04",
    title: "Execution",
    body: "Production management, quality control, and daily coordination in your timezone.",
  },
  {
    n: "05",
    title: "Delivery",
    body: "Export documentation, compliance, and clean handover to your operation.",
  },
  {
    n: "06",
    title: "Ongoing Partnership",
    body: "Capacity expansion, new SKUs, and cost optimisation over the long term.",
  },
];

export const QUICK_PROMPTS = [
  { slug: "sourcing-vs-alibaba", q: "How is MTS different from Alibaba or a marketplace?" },
  { slug: "verify-supplier", q: "How do you verify a supplier before I place an order?" },
  { slug: "qc-process", q: "What does your quality control process cover?" },
  { slug: "relocate-production", q: "Can you help relocate production to Asia?" },
  { slug: "start-project", q: "How do we start a project together?" },
];
